import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen, Clock, IndianRupee, Filter, X, FlaskConical, Calculator, Atom, Dna, CheckCircle, Hash, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';

const EXAM_TYPES = ['JEE Mains', 'JEE Advanced', 'NEET'];

const EXAM_CONFIG = {
  'JEE Mains': {
    subjects: ['Physics', 'Chemistry', 'Mathematics'],
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30'
  },
  'JEE Advanced': {
    subjects: ['Physics', 'Chemistry', 'Mathematics'],
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30'
  },
  'NEET': {
    subjects: ['Physics', 'Chemistry', 'Biology'],
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30'
  }
};

const SUBJECT_ICONS = {
  'Physics': Atom,
  'Chemistry': FlaskConical,
  'Mathematics': Calculator,
  'Biology': Dna
};

const QUESTION_TYPE_ICONS = {
  'MCQ': CheckCircle,
  'Numerical': Hash,
  'Multiple Choice': ListChecks
};

export default function TestSeriesBrowse() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [testSeries, setTestSeries] = useState([]);
  const [purchasedIds, setPurchasedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [selectedExam, setSelectedExam] = useState(searchParams.get('exam') || '');
  const [selectedSubject, setSelectedSubject] = useState(searchParams.get('subject') || '');
  const [showFilters, setShowFilters] = useState(false);

  const fetchTestSeries = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/test-series';
      const params = new URLSearchParams();
      if (selectedExam) params.append('exam_type', selectedExam);
      if (selectedSubject) params.append('subject', selectedSubject);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      setTestSeries(response.data);
      
      if (user) {
        try {
          const purchasesResponse = await api.get('/user/purchases');
          const purchasedTestIds = purchasesResponse.data.map(p => p.id);
          setPurchasedIds(purchasedTestIds);
        } catch (error) {
          // User might not have purchases, that's okay
        }
      }
    } catch (error) {
      toast.error('Failed to load test series');
    } finally {
      setLoading(false);
    }
  }, [selectedExam, selectedSubject, user]);

  useEffect(() => {
    fetchTestSeries();
  }, [fetchTestSeries]);

  useEffect(() => {
    // Update URL params
    const params = new URLSearchParams();
    if (selectedExam) params.set('exam', selectedExam);
    if (selectedSubject) params.set('subject', selectedSubject);
    setSearchParams(params);
  }, [selectedExam, selectedSubject, setSearchParams]);

  const getDifficultyColor = (difficulty) => {
    switch(difficulty?.toLowerCase()) {
      case 'easy': return 'text-success bg-success/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'hard': return 'text-destructive bg-destructive/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getExamColor = (examType) => {
    return EXAM_CONFIG[examType]?.color || 'from-gray-500 to-gray-600';
  };

  const clearFilters = () => {
    setSelectedExam('');
    setSelectedSubject('');
  };

  const availableSubjects = selectedExam ? EXAM_CONFIG[selectedExam]?.subjects || [] : 
    [...new Set(Object.values(EXAM_CONFIG).flatMap(c => c.subjects))];

  // Helper to get exam type from test (handles legacy category field)
  const getTestExamType = (test) => {
    if (test.exam_type) return test.exam_type;
    // Map legacy category values
    const categoryMapping = {
      'JEE Main': 'JEE Mains',
      'JEE Advanced': 'JEE Advanced',
      'NEET': 'NEET'
    };
    return categoryMapping[test.category] || test.category || 'JEE Mains';
  };

  // Group tests by exam type for display when no filter
  const groupedTests = !selectedExam ? EXAM_TYPES.reduce((acc, exam) => {
    acc[exam] = testSeries.filter(t => getTestExamType(t) === exam);
    return acc;
  }, {}) : null;

  return (
    <div className="min-h-screen bg-background" data-testid="test-series-browse-page">
      {/* Navbar */}
      <nav className="border-b border-border backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b913f950-9aae-49dc-aba5-d378a13b23f2/images/cfb7f6d255e29e51a76f73861f035a7bbb40bada25de0eb5061520d6d7d4dab1.png" 
            alt="Pinaka Logo"
            className="h-10 w-auto cursor-pointer"
            onClick={() => navigate('/')}
          />
          <div className="flex gap-3 items-center">
            <ThemeToggle />
            {user ? (
              <Button onClick={() => navigate('/dashboard')} data-testid="nav-dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} data-testid="nav-login-btn">
                Login
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h2 className="text-4xl font-outfit font-bold mb-2">Test Series</h2>
          <p className="text-lg text-muted-foreground">Choose from JEE Mains, JEE Advanced & NEET practice tests</p>
        </motion.div>

        {/* Exam Type Tabs */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3 mb-4">
            <Button
              variant={!selectedExam ? "default" : "outline"}
              onClick={() => { setSelectedExam(''); setSelectedSubject(''); }}
              className="font-semibold"
              data-testid="filter-all"
            >
              All Exams
            </Button>
            {EXAM_TYPES.map(exam => (
              <Button
                key={exam}
                variant={selectedExam === exam ? "default" : "outline"}
                onClick={() => { setSelectedExam(exam); setSelectedSubject(''); }}
                className={`font-semibold ${selectedExam === exam ? `bg-gradient-to-r ${getExamColor(exam)}` : ''}`}
                data-testid={`filter-${exam.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {exam}
              </Button>
            ))}
          </div>

          {/* Subject Filters */}
          {selectedExam && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="flex flex-wrap gap-2 mb-4"
            >
              <span className="text-sm text-muted-foreground self-center mr-2">Subject:</span>
              <Button
                size="sm"
                variant={!selectedSubject ? "secondary" : "ghost"}
                onClick={() => setSelectedSubject('')}
              >
                All
              </Button>
              {availableSubjects.map(subject => {
                const SubjectIcon = SUBJECT_ICONS[subject] || BookOpen;
                return (
                  <Button
                    key={subject}
                    size="sm"
                    variant={selectedSubject === subject ? "secondary" : "ghost"}
                    onClick={() => setSelectedSubject(subject)}
                    className="gap-1"
                  >
                    <SubjectIcon className="w-4 h-4" />
                    {subject}
                  </Button>
                );
              })}
            </motion.div>
          )}

          {/* Active Filters Display */}
          {(selectedExam || selectedSubject) && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Showing:</span>
              {selectedExam && (
                <span className={`px-2 py-1 rounded-full ${EXAM_CONFIG[selectedExam]?.bgColor} ${EXAM_CONFIG[selectedExam]?.borderColor} border`}>
                  {selectedExam}
                </span>
              )}
              {selectedSubject && (
                <span className="px-2 py-1 rounded-full bg-primary/10 border border-primary/30">
                  {selectedSubject}
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading test series...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Show grouped view when no exam filter */}
            {!selectedExam ? (
              <div className="space-y-12">
                {EXAM_TYPES.map(examType => {
                  const tests = groupedTests[examType] || [];
                  if (tests.length === 0) return null;
                  
                  return (
                    <motion.div
                      key={examType}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-8 rounded-full bg-gradient-to-b ${getExamColor(examType)}`}></div>
                          <h3 className="text-2xl font-outfit font-bold">{examType}</h3>
                          <span className="text-muted-foreground">({tests.length} tests)</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          onClick={() => setSelectedExam(examType)}
                          className="text-primary"
                        >
                          View All →
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {tests.slice(0, 3).map((test, index) => (
                          <TestCard 
                            key={test.id} 
                            test={test} 
                            index={index}
                            isPurchased={purchasedIds.includes(test.id)}
                            navigate={navigate}
                            getDifficultyColor={getDifficultyColor}
                          />
                        ))}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              /* Filtered view */
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testSeries.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <p className="text-muted-foreground text-lg">No tests found for the selected filters.</p>
                    <Button variant="outline" onClick={clearFilters} className="mt-4">
                      Clear Filters
                    </Button>
                  </div>
                ) : (
                  testSeries.map((test, index) => (
                    <TestCard 
                      key={test.id} 
                      test={test} 
                      index={index}
                      isPurchased={purchasedIds.includes(test.id)}
                      navigate={navigate}
                      getDifficultyColor={getDifficultyColor}
                    />
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Test Card Component
function TestCard({ test, index, isPurchased, navigate, getDifficultyColor }) {
  const SubjectIcon = SUBJECT_ICONS[test.subject] || BookOpen;
  const QuestionTypeIcon = QUESTION_TYPE_ICONS[test.question_type] || CheckCircle;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -6 }}
      className={`bg-card border rounded-xl overflow-hidden hover:border-primary/50 transition-all cursor-pointer group ${
        isPurchased ? 'border-success/30' : 'border-border'
      }`}
      onClick={() => navigate(isPurchased ? `/test/${test.id}` : `/test-series/${test.id}`)}
      data-testid={`test-card-${test.id}`}
    >
      {test.thumbnail_url && (
        <div className="relative overflow-hidden h-40">
          <img 
            src={test.thumbnail_url} 
            alt={test.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
          />
          <div className="absolute top-3 right-3 flex gap-2">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getDifficultyColor(test.difficulty)}`}>
              {test.difficulty}
            </span>
          </div>
          {isPurchased && (
            <div className="absolute top-3 left-3 bg-success/90 backdrop-blur-sm px-2 py-1 rounded-full">
              <span className="text-xs font-semibold text-white">Owned</span>
            </div>
          )}
          {test.is_free && !isPurchased && (
            <div className="absolute top-3 left-3 bg-primary/90 backdrop-blur-sm px-2 py-1 rounded-full">
              <span className="text-xs font-semibold text-white">FREE</span>
            </div>
          )}
        </div>
      )}
      
      <div className="p-5">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
            EXAM_CONFIG[test.exam_type]?.bgColor || 'bg-muted'
          } ${EXAM_CONFIG[test.exam_type]?.borderColor || ''} border`}>
            {test.exam_type || test.category}
          </span>
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-muted flex items-center gap-1">
            <SubjectIcon className="w-3 h-3" />
            {test.subject || 'General'}
          </span>
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-muted flex items-center gap-1">
            <QuestionTypeIcon className="w-3 h-3" />
            {test.question_type || 'MCQ'}
          </span>
        </div>

        <h3 className="text-lg font-outfit font-bold mb-2 group-hover:text-primary transition-colors line-clamp-1">
          {test.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{test.description}</p>
        
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
          <div className="flex items-center gap-1">
            <BookOpen className="w-4 h-4" />
            <span>{test.questions_count} Qs</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{test.duration} min</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          {isPurchased ? (
            <span className="text-success font-semibold text-sm">Already Purchased</span>
          ) : test.is_free ? (
            <span className="text-primary font-bold">FREE</span>
          ) : (
            <div className="flex items-center gap-1 text-xl font-bold font-mono">
              <IndianRupee className="w-4 h-4" />
              <span>{test.price}</span>
            </div>
          )}
          <Button size="sm" data-testid={`view-details-${test.id}`}>
            {isPurchased ? 'Start' : 'View'}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
